import type { QueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import cacheKeys from "@/config/cache-keys";
import { accountTreeWithMergedInsuranceParams } from "@/lib/accountTreeToUpdatePayload";
import { computeProductDrift } from "@/lib/insuranceDrift";
import { mergeInsuranceParamsWithCatalogTemplate } from "@/lib/mergeInsuranceParamsWithCatalogTemplate";
import type {
	AccountTree,
	UpdateAccountTree,
} from "@/types/accountTree/accountTree";
import type { CreateInsuranceFormData } from "@/types/insurance/insurance";

type TreeService = {
	getAllAccounts: () => Promise<{
		Result?: ReadonlyArray<{ main_account_id: string }>;
	}>;
	getMainAccountTree: (id: string) => Promise<{ Result?: AccountTree }>;
	updateMainAccountTree: (
		id: string,
		data: UpdateAccountTree,
	) => Promise<unknown>;
};

export const dedupeMainAccountIds = (
	accounts: ReadonlyArray<{ main_account_id: string }>,
): string[] => {
	const seen = new Set<string>();
	const ids: string[] = [];
	for (const acc of accounts) {
		if (!seen.has(acc.main_account_id)) {
			seen.add(acc.main_account_id);
			ids.push(acc.main_account_id);
		}
	}
	return ids;
};

export const buildDriftCountByType = (
	trees: ReadonlyArray<AccountTree | undefined>,
	templates: ReadonlyArray<CreateInsuranceFormData>,
): Map<string, number> => {
	const countByType = new Map<string, number>();
	for (const tree of trees) {
		if (!tree) continue;
		const report = computeProductDrift(tree.insurances, templates);
		for (const d of report.drifted) {
			countByType.set(d.type, (countByType.get(d.type) ?? 0) + 1);
		}
	}
	return countByType;
};

export const fetchDriftCountByTypeMap = async (
	appService: TreeService,
	templates: ReadonlyArray<CreateInsuranceFormData>,
): Promise<Map<string, number>> => {
	const acc = await appService.getAllAccounts();
	const ids = dedupeMainAccountIds(acc.Result ?? []);
	const trees = await Promise.all(
		ids.map(async (id) => {
			try {
				const r = await appService.getMainAccountTree(id);
				return r.Result;
			} catch {
				return undefined;
			}
		}),
	);
	return buildDriftCountByType(trees, templates);
};

export type SyncDisabledDriftFromTreesResult = {
	updatedAccounts: number;
	failedAccounts: number;
};

/**
 * For each account tree, merges catalog drift for the given product type for
 * structurally drifted rows. By default only rows with no employee groups are
 * included (`onlyRowsWithoutGroups: true`). When `false`, all drifted rows for
 * that product type are merged (can affect live coverage — caller must confirm).
 */
export const syncDisabledDriftFromTrees = async (opts: {
	productType: string;
	templates: ReadonlyArray<CreateInsuranceFormData>;
	trees: ReadonlyArray<AccountTree | undefined>;
	appService: TreeService;
	queryClient: QueryClient;
	/** When true (default), skip insurance rows that have any group assigned. */
	onlyRowsWithoutGroups?: boolean;
}): Promise<SyncDisabledDriftFromTreesResult> => {
	let updatedAccounts = 0;
	let failedAccounts = 0;

	const onlyRowsWithoutGroups = opts.onlyRowsWithoutGroups ?? true;

	const template = opts.templates.find((d) => d.type === opts.productType);
	if (!template) {
		return { updatedAccounts, failedAccounts };
	}

	for (const tree of opts.trees) {
		if (!tree) continue;

		const report = computeProductDrift(tree.insurances, opts.templates);
		const driftedIds = new Set(
			report.drifted
				.filter((d) => d.type === opts.productType)
				.map((d) => d.id),
		);
		const rowsToFix = tree.insurances.filter((ins) => {
			if (ins.type !== opts.productType || !driftedIds.has(ins.id)) {
				return false;
			}
			if (onlyRowsWithoutGroups && (ins.groups?.length ?? 0) > 0) {
				return false;
			}
			return true;
		});
		if (rowsToFix.length === 0) continue;

		const mergedById = new Map(
			rowsToFix.map((ins) => [
				ins.id,
				mergeInsuranceParamsWithCatalogTemplate(ins, template),
			]),
		);

		const payload = accountTreeWithMergedInsuranceParams(tree, mergedById);

		try {
			await opts.appService.updateMainAccountTree(
				tree.main_account.main_account_id,
				payload,
			);
			updatedAccounts += 1;
			await opts.queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_MAIN_ACCOUNT(
					tree.main_account.main_account_id,
				),
			});
		} catch (e) {
			failedAccounts += 1;
			console.error(
				`[syncDisabledDriftFromTrees] sync failed for ${tree.main_account.display_name}:`,
				e,
			);
		}
	}

	return { updatedAccounts, failedAccounts };
};

export const emitSyncDisabledDriftResultToasts = (
	t: TFunction,
	updatedAccounts: number,
	failedAccounts: number,
) => {
	if (updatedAccounts === 0 && failedAccounts === 0) {
		toast.info(t("admin.product-definitions.drift-modal.sync-nothing-done"));
		return;
	}

	if (failedAccounts === 0) {
		toast.success(
			t("admin.product-definitions.drift-modal.sync-success", {
				count: updatedAccounts,
			}),
		);
	} else if (updatedAccounts > 0) {
		toast.warning(
			t("admin.product-definitions.drift-modal.sync-partial", {
				ok: updatedAccounts,
				fail: failedAccounts,
			}),
		);
	} else {
		toast.error(
			t("admin.product-definitions.drift-modal.sync-all-failed", {
				count: failedAccounts,
			}),
		);
	}
};
